import { BrowserRouter, Route, Routes } from 'react-router-dom'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'
import { StyledEngineProvider } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { ConfirmProvider } from 'material-ui-confirm'
import './index.css'

import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import AuthBoundary from './components/AuthBoundary'
import PublicShare from './pages/PublicShare'
import Terms from './pages/Terms'
import Contact from './pages/Contact'
import { CONTACT_PATH, TERMS_PATH } from './constants/legal'
import AppShell from './components/AppShell'
import { isLocalE2E } from './config/e2e'
import { AuthProvider } from './auth/AuthContext'

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#55b8d8',
      light: '#87d0e7'
    },
    success: {
      main: '#67c587'
    },
    background: {
      default: '#070a0e',
      paper: '#11161d'
    },
    divider: 'rgba(255,255,255,.09)'
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    button: {
      textTransform: 'none',
      fontWeight: 650
    }
  },
  shape: {
    borderRadius: 8
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none'
        }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          borderColor: 'rgba(255,255,255,.09)'
        }
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255,255,255,.025)'
        }
      }
    },
    MuiTooltip: {
      defaultProps: {
        arrow: true
      }
    }
  }
})

export default function Index () {
  const e2eAuthenticated = isLocalE2E()

  return (
    <AuthProvider>
      <LocalizationProvider dateAdapter={AdapterLuxon}>
        <BrowserRouter>
          <Routes>
            {/* Owner-opened no-login links (D14) must render without signing in. */}
            <Route path="/watch/:token" element={<PublicShare />} />
            <Route path={TERMS_PATH} element={<Terms />} />
            <Route path={CONTACT_PATH} element={<Contact />} />
            <Route
              path="*"
              element={e2eAuthenticated ? <AppShell testMode /> : <AuthBoundary />}
            />
          </Routes>
        </BrowserRouter>
      </LocalizationProvider>
    </AuthProvider>
  )
}

const container = document.getElementById('app')
const root = createRoot(container)
root.render(
  <React.StrictMode>
    <ThemeProvider theme={darkTheme}>
      <CssBaseline enableColorScheme />
      <StyledEngineProvider injectFirst>
        <ConfirmProvider>
          <Index />
        </ConfirmProvider>
      </StyledEngineProvider>
    </ThemeProvider>
  </React.StrictMode>
)
